import { formatDate, formatRelative } from '../../lib/format'
import type { OrgsView } from '../../types/orgs'
import type { SettingField } from '../../types/settings'
import { overriddenCount } from './groups'

// The firm at a glance, before any of the controls: how many orgs there are, whether anybody is left
// unplaced, how much of the deployment this firm has changed since it shipped, and when the pipeline
// next refreshes itself. Every number here is something the API sent; nothing is estimated.

export interface GlanceProps {
  fields: SettingField[]
  orgs: OrgsView | null
  /** the nightly chain's next due moment, from the schedule surface, or null when the cadence is off */
  nextDue: string | null
  cadence: string
}

export function Glance({ fields, orgs, nextDue, cadence }: GlanceProps) {
  const placed = orgs ? orgs.orgs.reduce((n, o) => n + o.members.length, 0) : 0
  const unplaced = orgs?.unassigned.length ?? 0
  const overridden = overriddenCount(fields)

  return (
    <section className="glance settings-glance">
      <div className="glance-stat">
        <strong>{orgs?.orgs.length ?? '—'}</strong>
        <span>{orgs?.orgs.length === 1 ? 'organisation' : 'organisations'} in this tenant</span>
      </div>
      <div className="glance-stat">
        <strong>{placed}</strong>
        <span>
          {placed === 1 ? 'person placed' : 'people placed'}
          {unplaced > 0 ? ` · ${unplaced} not in an org` : ' · nobody left out'}
        </span>
      </div>
      <div className="glance-stat">
        <strong>{overridden}</strong>
        <span>of {fields.length} settings changed since the deploy</span>
      </div>
      <div className="glance-stat">
        <strong>{cadence === 'off' ? 'Off' : nextDue ? formatRelative(nextDue) : '—'}</strong>
        <span>
          {cadence === 'off'
            ? 'the pipeline refreshes only when somebody presses it'
            : nextDue
              ? `next ${cadence} run · ${formatDate(nextDue)}`
              : `${cadence} run, not yet scheduled`}
        </span>
      </div>
    </section>
  )
}
